import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Injector,
    signal,
    Signal,
    ViewEncapsulation
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import {
    Cart,
    CartPageStore,
    selectCart,
    selectCartTable,
    selectIsCartShared,
    selectOrderType
} from '@us/web/cart/data-access';
import { OrderActionsComponent, OrderQrcodeModalComponent, ShareOrderBtnComponent } from '@us/web/orders/feature';
import { NgForTrackByPropertyDirective, OrderType, Table } from '@us/web/shared/data-access';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { TuiButtonModule, TuiDialogService, TuiSvgModule } from '@taiga-ui/core';
import { TuiSheetModule, TuiSheetOptions } from '@taiga-ui/addon-mobile';
import { OrderQrcodeComponent } from '@us/web/orders/ui';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { CartAtTheTableComponent } from '../cart-at-the-table/cart-at-the-table.component';
import { TuiLetModule } from '@taiga-ui/cdk';
import { CartDeliveryComponent } from '../cart-delivery/cart-delivery.component';
import { TuiAvatarModule } from '@taiga-ui/kit';

@UntilDestroy()
@Component({
    selector: 'us-cart-page',
    templateUrl: './cart.page.html',
    styleUrls: ['./cart.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        IonicModule,
        TranslocoModule,
        TuiSheetModule,
        OrderActionsComponent,
        TuiSvgModule,
        OrderQrcodeComponent,
        CartAtTheTableComponent,
        NgIf,
        AsyncPipe,
        TuiLetModule,
        CartDeliveryComponent,
        TuiButtonModule,
        TuiAvatarModule,
        NgForOf,
        NgForTrackByPropertyDirective,
        ShareOrderBtnComponent
    ],
    standalone: true,
    providers: []
})
export class CartPage {
    private readonly store = inject(Store);
    private readonly injector = inject(Injector);
    private readonly dialogService = inject(TuiDialogService);
    private readonly cartStore = inject(CartPageStore);

    readonly cart: Signal<Cart> = this.store.selectSignal(selectCart);
    readonly cartTable: Signal<Table | undefined> = this.store.selectSignal(selectCartTable);
    readonly orderType: Signal<OrderType | null> = this.store.selectSignal(selectOrderType);
    readonly isCartOwner: Signal<boolean> = this.cartStore.isCartOwner;
    readonly isCartShared = this.store.selectSignal(selectIsCartShared);

    readonly cartTableName: Signal<string | null> = computed(() => {
        const table = this.cartTable();
        return table ? table.area_name + '-' + table.name : null;
    });
    readonly orderCode: Signal<string> = computed(() => this.cart().order_code);
    readonly versionCode: Signal<number> = computed(() => this.cart().version_code);

    readonly OrderType = OrderType;

    readonly showActionsSheet = signal(false);

    readonly options: Partial<TuiSheetOptions> = {
        overlay: true
    };

    toggleCartActionsSheet() {
        this.showActionsSheet.update(show => !show);
    }

    toggleCartQrCodeSheet() {
        this.dialogService
            .open<void>(
                new PolymorpheusComponent(OrderQrcodeModalComponent, this.injector),
                {
                    dismissible: true,
                    // overlay: true,
                    data: {
                        order: this.cart()
                    }
                }
                //  as unknown as Partial<TuiSheetOptions>
            )
            .subscribe();
    }
}
